/**
 * Lamp generator — a floor lamp built from cylinder geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X/Y = horizontal extent
 *   - Z = height
 *
 * All parts are cylinders so this generator requests `"autoSmooth"` shading
 * (via `shadingForGenerator` in `storeGeneratorOps.ts`) for a clean, smooth
 * appearance without per-face shading seams on the rounded surfaces.
 *
 * Geometry is built as separate `GeneratorChild` entries (kind: "group") so
 * the individual parts can be selected and adjusted independently. Each child
 * carries its own cylinder geometry in local space; the group parent sits at
 * the world cursor position.
 */

import {
  buildCylinder,
  concatPrimitiveGeometries,
  transformGeometry,
} from "../generators/generatorGeom.ts";
import type { PrimitiveGeometry } from "../primitives.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorChild,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import { defineGenerator, numParam, paramsFromSpecs } from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface LampParams extends GeneratorParams {
  poleHeight: number;
  poleRadius: number;
  baseRadius: number;
  baseHeight: number;
  shadeRadius: number;
  shadeHeight: number;
  shadeTopRatio: number;
}

const LAMP_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "lamp-base",
    label: "Base",
    params: [
      {
        id: "baseRadius",
        label: "Base Radius",
        type: "number",
        unit: "meters",
        default: 0.18,
        min: 0.05,
        max: 0.5,
        step: 0.01,
      },
      {
        id: "baseHeight",
        label: "Base Height",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.01,
        max: 0.1,
        step: 0.005,
      },
    ],
  },
  {
    type: "section",
    id: "lamp-pole",
    label: "Pole",
    params: [
      {
        id: "poleHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 1.4,
        min: 0.05,
        max: 2.5,
        step: 0.025,
      },
      {
        id: "poleRadius",
        label: "Radius",
        type: "number",
        unit: "meters",
        default: 0.015,
        min: 0.005,
        max: 0.05,
        step: 0.002,
      },
    ],
  },
  {
    type: "section",
    id: "lamp-shade",
    label: "Shade",
    params: [
      {
        id: "shadeRadius",
        label: "Shade Radius",
        type: "number",
        unit: "meters",
        default: 0.22,
        min: 0.05,
        max: 0.6,
        step: 0.01,
      },
      {
        id: "shadeHeight",
        label: "Shade Height",
        type: "number",
        unit: "meters",
        default: 0.28,
        min: 0.05,
        max: 0.6,
        step: 0.01,
      },
      {
        id: "shadeTopRatio",
        label: "Top Narrowing",
        type: "number",
        default: 0.4,
        min: 0.1,
        max: 0.9,
        step: 0.05,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

function buildTaperedShade(
  bottomRadius: number,
  topRatio: number,
  height: number,
  segments = 24,
): ReturnType<typeof buildCylinder> {
  const slices = 6;
  const topRadius = Math.max(0.005, bottomRadius * topRatio);
  const parts: PrimitiveGeometry[] = [];
  for (let i = 0; i < slices; i++) {
    const t0 = i / slices;
    const t1 = (i + 1) / slices;
    const r0 = bottomRadius + (topRadius - bottomRadius) * t0;
    const r1 = bottomRadius + (topRadius - bottomRadius) * t1;
    const radius = (r0 + r1) * 0.5;
    const sliceH = height / slices;
    const z = -height * 0.5 + sliceH * (i + 0.5);
    parts.push(
      transformGeometry(buildCylinder(radius, sliceH, segments), [0, 0, z]),
    );
  }
  return concatPrimitiveGeometries(parts);
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function buildLamp(
  p: LampParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const poleH = numParam(p, "poleHeight", 1.4);
  const poleR = numParam(p, "poleRadius", 0.015);
  const baseR = numParam(p, "baseRadius", 0.18);
  const baseH = numParam(p, "baseHeight", 0.04);
  const shadeR = numParam(p, "shadeRadius", 0.22);
  const shadeH = numParam(p, "shadeHeight", 0.28);
  const shadeTop = numParam(p, "shadeTopRatio", 0.4);

  const children: GeneratorChild[] = [];
  const shadeGeometry = buildTaperedShade(shadeR, shadeTop, shadeH);

  // Base disc — sits flat on the floor (z = 0 to baseH)
  children.push({
    roleId: "base",
    name: "Base",
    objectKind: "imported",
    geometry: buildCylinder(baseR, baseH),
    localTransform: {
      position: [0, 0, baseH * 0.5],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
    },
  });

  // Pole
  const poleBottomZ = baseH;
  const poleCentreZ = poleBottomZ + poleH * 0.5;
  children.push({
    roleId: "pole",
    name: "Pole",
    objectKind: "imported",
    geometry: buildCylinder(poleR, poleH),
    localTransform: {
      position: [0, 0, poleCentreZ],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
    },
  });

  const shadeCentreZ = poleBottomZ + poleH + shadeH * 0.5;
  children.push({
    roleId: "shade",
    name: "Shade",
    objectKind: "imported",
    geometry: shadeGeometry,
    localTransform: {
      position: [0, 0, shadeCentreZ],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
    },
  });

  return {
    kind: "group",
    parent: { name: "Lamp" },
    children,
  };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const LAMP_GENERATOR: Generator = defineGenerator<LampParams>({
  id: "lamp",
  label: "Lamp",
  category: "prop",
  paramSpecs: LAMP_SPECS,
  defaultParams: paramsFromSpecs(LAMP_SPECS) as LampParams,
  build: buildLamp,
});
